import React from 'react'
import { Table, ControlledTooltip, Button } from 'components'
import styles from './MultiPartyTable.module.scss'
import AddOn from 'views/components/atoms/Svgs/AddOn'
import { portfolioEvents } from 'modules/portfolios/state'
import { useDispatch } from 'react-redux'

interface MultiPartyMember {
  id: string
  name: string
  email: string
  isPrimary: boolean
}

interface MultiPartyTableProps {
  data: MultiPartyMember[]
  onEdit: (id: string) => void
  onRemove: (id: string) => void
  onMakePrimary: (id: string) => void
  allowEdit: boolean
  partyType?: string
}

interface ActionButtonProps {
  tooltip: string
  icon: string
  action: () => void
  disabled?: boolean
}

const ActionButton = ({ tooltip, icon, action, disabled = false }: ActionButtonProps) => (
  <ControlledTooltip body={tooltip} place="above">
    <Button disabled={disabled} ghost className={styles[icon]} onClick={action} />
  </ControlledTooltip>
)

const MultiPartyTable = ({ data, onEdit, onMakePrimary, onRemove, allowEdit, partyType }: MultiPartyTableProps) => {
  const dispatch = useDispatch()

  const multiPartyData = data.map((party: MultiPartyMember) => {
    const { id, name, email, isPrimary } = party

    return {
      id,
      partyDetails: (
        <span className={styles['party-details']}>
          <strong>{name}</strong>
          <span className={styles.subtext}>{email}</span>
        </span>
      ),
      actions: (
        <span className={styles.actions}>
          <ActionButton
            tooltip={
              isPrimary && partyType === 'owner'
                ? 'Primary owner receives rental income and is the primary contact for all communication and statements.'
                : isPrimary && partyType === 'tenant'
                ? 'Primary tenant receives all invoices and is the primary contact for all communication.'
                : 'Make primary'
            }
            icon={isPrimary ? 'primary-fill' : 'primary'}
            action={isPrimary ? () => {} : () => onMakePrimary(id)}
            disabled={!allowEdit}
          />
          <ActionButton tooltip="Edit contact" icon="edit" action={() => onEdit(id)} />
          {data.length > 1 && (
            <ActionButton
              tooltip="Remove from list"
              icon="remove"
              action={() => onRemove(id)}
              disabled={isPrimary || !allowEdit}
            />
          )}
        </span>
      ),
    }
  })

  return (
    <div className={styles.root}>
      {multiPartyData.length > 0 ? (
        <>
          <Table type="default">
            <Table.Body>
              {multiPartyData.map((party, i) => (
                <Table.Row key={i}>
                  <Table.Cell style={{ paddingLeft: '30px' }}>{party.partyDetails}</Table.Cell>
                  <Table.Cell style={{ paddingRight: '30px', justifyContent: 'flex-end' }}>{party.actions}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      ) : (
        <div className={styles['no-content']}>
          <p>
            {partyType
              ? `No ${partyType}s yet. At least one ${partyType} is required to continue.`
              : 'No parties yet. At least one party is required to continue.'}
          </p>
          <Button className={styles['add-party']} onClick={() => dispatch(portfolioEvents.togglePartyModal())}>
            <AddOn fill="#fff" />
            Add {partyType}
          </Button>
        </div>
      )}
    </div>
  )
}

export default MultiPartyTable
